import React from 'react';
import { motion } from 'framer-motion';
import HorizontalImageCarousel from '../components/HorizontalImageCarousel';

const FinancialPerformance = () => {
  // Table Data
  const revenueData = [
    { year: "2015", revenue: "1.0 Million", growth: "Baseline" },
    { year: "2016", revenue: "1.8 Million", growth: "+80%" },
    { year: "2017", revenue: "3.2 Million", growth: "+78%" },
    { year: "2018", revenue: "5.5 Million", growth: "+72%" },
    { year: "2019", revenue: "7.8 Million", growth: "+42%" },
    { year: "2020", revenue: "10.5 Million", growth: "+35%" },
    { year: "2021", revenue: "13.2 Million", growth: "+26%" },
    { year: "2022", revenue: "16.5 Million", growth: "+25%" },
    { year: "2023", revenue: "18.7 Million", growth: "+13%" },
    { year: "2024", revenue: "18.7 Million", growth: "+24%" },
    { year: "2025", revenue: "18.7 Million", growth: "+29%" },

  ];

  // Strengths Data
  const strengths = [
    "Consistent year-over-year revenue growth demonstrating market strength and client confidence",
    "Strong balance sheet with minimal debt and healthy cash reserves",
    "Diversified revenue streams across multiple geographic markets and sectors",
    "Strong profit margins through efficient operations and value engineering",
    "Robust project pipeline with over 200 million in contracted work",
    "Strong bonding capacity enabling pursuit of large-scale projects"
  ];

  // Animation Variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="font-sans text-slate-800 bg-white" style={{ fontFamily: "Monstserrat, proxima-nova, Helveti, sans-serif" }}>
      {/* 1. HERO IMAGE SECTION (No Text) */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="relative w-full h-72 md:h-[500px] bg-slate-900 overflow-hidden"
      >
        <img
          src='/images/FN.jpg'
          alt="Financial Growth"
          className="w-full h-full object-cover opacity-90 hover:scale-80 transition-transform duration-[2000ms]"
        />
        {/* Overlay gradient only for aesthetics, no text */}
        {/* <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" /> */}
      </motion.div>

      <div className="max-w-6xl mx-auto px-4 py-16">

        {/* 2. HEADER & INTRO TEXT */}
        <motion.div
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16 text-center max-w-4xl mx-auto"
        >
          {/* Main Title Added Here since it was removed from Image */}
          <h1 className="text-4xl md:text-5xl font-semibold mb-6 text-slate-800 tracking-tight">
            Financial Performance & Growth
          </h1>
          <h2 className="text-xl md:text-2xl font-medium mb-6 text-gray-600">
            Remarkable Growth Trajectory
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed font-light">
            TM International Group has achieved remarkable financial growth since 2016,
            Expending into <span className="font-bold text-slate-800"> multi million</span> revenue company in recent years.
            This represents a <span className="font-bold text-green-600">150%-200% increase</span> annually, reflecting our strategic market positioning,
            operational excellence, and ability to capture high-value project opportunities.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">

          {/* 3. REVENUE TABLE */}
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-white shadow-xl border border-slate-100 overflow-hidden"
          >
            <div className="bg-slate-800 py-5 px-6">
              <h3 className="text-white text-xl font-medium tracking-wide flex items-center">
                <span className="bg-green-500 w-2 h-6 mr-3 rounded-full"></span>
                Revenue Growth (2016-2024)
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="py-4 px-6 font-semibold text-slate-700">Year</th>
                    {/* <th className="py-4 px-6 font-semibold text-slate-700">Annual Revenue</th> */}
                    <th className="py-4 px-6 font-semibold text-slate-700">Growth Rate</th>
                  </tr>
                </thead>
                <motion.tbody
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="divide-y divide-slate-100"
                >
                  {revenueData.map((row, index) => (
                    <motion.tr
                      key={index}
                      variants={itemVariants}
                      className={`hover:bg-blue-50 transition-colors duration-150 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                    >
                      <td className="py-3 px-6 text-slate-800 font-medium">{row.year}</td>
                      {/* <td className="py-3 px-6 text-slate-600">{row.revenue}</td> */}
                      <td className={`py-3 px-6 font-medium ${row.growth === 'Baseline' ? 'text-slate-400' : 'text-green-600'}`}>
                        {row.growth}
                      </td>
                    </motion.tr>
                  ))}
                </motion.tbody>
              </table>
            </div>
          </motion.div>

          {/* 4. FINANCIAL STRENGTHS LIST */}
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-slate-50 p-8 border border-slate-200 h-full relative"
          >
            <div className="absolute top-0 right-0 p-4 opacity-10">
              {/* Background decoration icon */}
              <svg width="120" height="120" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05 1.18 1.91 2.53 1.91 1.33 0 2.26-.87 2.26-2.02 0-1.13-.95-1.58-2.28-2.08l-.89-.32c-1.98-.75-3.04-2.02-3.04-3.5 0-1.85 1.43-3.02 3.16-3.32V3h2.67v1.93c1.38.3 2.48 1.15 2.66 2.8h-1.92c-.11-.78-.79-1.51-2.07-1.51-1.16 0-1.93.71-1.93 1.66 0 1.05.9 1.45 2.45 2.03l.89.33c2.2.82 3 2.15 3 3.66 0 2.05-1.55 3.18-3.49 3.51z" />
              </svg>
            </div>

            <h3 className="text-2xl font-bold text-slate-800 mb-8 border-b w-80 border-slate-200 pb-4 relative z-10">
              Financial Strengths
            </h3>
            <motion.ul
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-6 relative z-10"
            >
              {strengths.map((item, index) => (
                <motion.li
                  key={index}
                  variants={itemVariants}
                  className="flex items-start group"
                >
                  <div className="flex-shrink-0 mt-1 transition-transform duration-300 group-hover:scale-110">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shadow-sm">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </div>
                  </div>
                  <span className="ml-4 text-slate-700 text-lg leading-relaxed font-light group-hover:text-slate-900 transition-colors">
                    {item}
                  </span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

        </div>
      </div>
      <HorizontalImageCarousel />
    </div>
  );
};

export default FinancialPerformance;